import React, { useState, useEffect } from 'react';
import { callsAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatDuration } from '../utils/formatters';
import RatingStars from '../components/RatingStars';
import toast from 'react-hot-toast';

const PERIODS = [
  { key: 'today', label: 'Bugun' },
  { key: 'week', label: 'Bu hafta' },
  { key: 'month', label: 'Bu oy' },
  { key: 'year', label: 'Bu yil' },
];

// "Qancha soat gaplashgan" degan savolga TUSHUNARLI javob. `formatDuration`
// 18:40:20 beradi — bu hisobot uchun to'g'ri, lekin "18 soat 40 daqiqa" ni
// bir qarashda o'qish osonroq.
function humanHours(seconds) {
  const s = Number(seconds) || 0;
  if (s < 60) return `${s} soniya`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m} daqiqa`;
  return m > 0 ? `${h} soat ${m} daqiqa` : `${h} soat`;
}

export default function OperatorStatsPage() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    callsAPI.stats({ period })
      .then((res) => { if (alive) setStats(res.data); })
      .catch(() => { if (alive) toast.error("Ko'rsatkichlarni olishda xato"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const s = stats?.summary || {};
  const csat = stats?.csat || {};
  const dist = csat.distribution || {};
  // Eng ko'p uchragan baho — taqsimot ustunlarini shunga nisbatan chizamiz.
  const maxDist = Math.max(1, ...[1, 2, 3, 4, 5].map((k) => Number(dist[k]) || 0));

  const daily = (stats?.daily_calls || []).map((d) => ({
    kun: new Date(d.date).toLocaleDateString('uz-UZ', { month: 'short', day: 'numeric' }),
    'Javob berildi': parseInt(d.answered, 10) || 0,
  }));

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>Mening ko'rsatkichlarim</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Faqat sizning qo'ng'iroqlaringiz
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: '7px 14px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
                border: '1px solid ' + (period === p.key ? 'var(--accent, #3b82f6)' : 'var(--border)'),
                background: period === p.key ? 'var(--accent-light, rgba(59,130,246,0.15))' : 'transparent',
                color: period === p.key ? 'var(--accent, #3b82f6)' : 'var(--text-secondary)',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', marginBottom: 20 }}>
        <div className="stat-card">
          <span className="stat-label">Jami gaplashgan vaqt</span>
          <span className="stat-value" style={{ color: '#3b82f6' }}>{humanHours(s.total_talk_time)}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {formatDuration(parseInt(s.total_talk_time, 10) || 0)}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Javob berilgan</span>
          <span className="stat-value" style={{ color: '#10b981' }}>{s.answered || 0}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            kiruvchi {s.inbound || 0} · chiquvchi {s.outbound || 0}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">O'rtacha davomiylik</span>
          <span className="stat-value">{formatDuration(Math.round(Number(s.avg_duration) || 0))}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            eng uzun {formatDuration(parseInt(s.max_duration, 10) || 0)}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Mijoz bahosi</span>
          {csat.rated_count > 0 ? (
            <>
              <span className="stat-value" style={{ color: '#f59e0b' }}>{csat.avg_rating}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                <RatingStars value={csat.avg_rating} /> · {csat.rated_count} ta baho
              </span>
            </>
          ) : (
            <>
              <span className="stat-value" style={{ color: 'var(--text-muted)' }}>—</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>bu davrda baho yo'q</span>
            </>
          )}
        </div>
      </div>

      {csat.rated_count > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Baholar taqsimoti</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
            {[5, 4, 3, 2, 1].map((star) => {
              const n = Number(dist[star]) || 0;
              const pct = Math.round((n / maxDist) * 100);
              const color = star >= 4 ? '#10b981' : star === 3 ? '#f59e0b' : '#ef4444';
              return (
                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 54, fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {star} <span style={{ color }}>★</span>
                  </span>
                  <div style={{ flex: 1, height: 10, borderRadius: 5, background: 'var(--bg-primary, #0f172a)', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 5 }} />
                  </div>
                  <span style={{ width: 34, textAlign: 'right', fontSize: 13, fontWeight: 600 }}>{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {daily.length > 0 && (
        <div className="card">
          <div className="card-header"><span className="card-title">Kunlar bo'yicha javob berilgan qo'ng'iroqlar</span></div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2d3a4f" />
                <XAxis dataKey="kun" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #2d3a4f', borderRadius: 10 }} />
                <Bar dataKey="Javob berildi" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
